import React, { useState } from "react";
import { Link } from "react-router-dom";
import { siteConfig } from "../Datas/productData";
import "../css/contactus.scss";

const initialFormData = {
  firstName: "",
  lastName: "",
  email: "",
  subject: "",
  message: "",
};

function Contact() {
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedName, setSubmittedName] = useState("");
  const [errors, setErrors] = useState({});

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleInputChange = ({ target: { name, value } }) => {
    setFormData((current) => ({ ...current, [name]: value }));
    setSubmittedName("");
    if (errors[name]) {
      setErrors((current) => ({ ...current, [name]: "" }));
    }
  };

  const validateForm = () => {
    const nextErrors = {};
    if (formData.firstName.trim().length < 2) {
      nextErrors.firstName = "Enter at least 2 characters.";
    }
    if (formData.lastName.trim().length < 2) {
      nextErrors.lastName = "Enter at least 2 characters.";
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      nextErrors.email = "Enter a valid email address.";
    }
    if (!formData.subject) {
      nextErrors.subject = "Choose the topic that best fits your question.";
    }
    if (formData.message.trim().length < 20) {
      nextErrors.message = "Tell us a little more (at least 20 characters).";
    } else if (formData.message.length > 1200) {
      nextErrors.message = "Keep your message to 1,200 characters or fewer.";
    }

    return nextErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = validateForm();

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      const firstInvalidField = document.getElementById(Object.keys(nextErrors)[0]);
      firstInvalidField?.focus();
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    await new Promise((resolve) => window.setTimeout(resolve, 850));
    const subject = encodeURIComponent(`poodliving enquiry: ${formData.subject}`);
    const body = encodeURIComponent(`From: ${formData.firstName} ${formData.lastName}\nEmail: ${formData.email}\n\n${formData.message}`);
    window.location.href = `mailto:${siteConfig.contact.email}?subject=${subject}&body=${body}`;
    setSubmittedName(formData.firstName.trim());
    setFormData(initialFormData);
    setIsSubmitting(false);
  };

  const errorProps = (field) => ({
    "aria-invalid": Boolean(errors[field]),
    "aria-describedby": errors[field] ? `${field}-error` : undefined,
  });

  return (
    <main className="contact">
      <div className="contact__shell">
        <nav className="contact__breadcrumb" aria-label="Breadcrumb">
          <Link to="/" onClick={toTop}>Home</Link>
          <span aria-hidden="true">/</span>
          <span aria-current="page">Contact</span>
        </nav>

        <header className="contact__header">
          <p className="contact__kicker">Customer care</p>
          <h1>How can we help?</h1>
          <p>
            Product question, delivery update or return request—send us the details and
            we will point you in the right direction.
          </p>
        </header>

        <div className="contact__layout">
          <aside className="contact__details" aria-label="Contact details">
            <p className="contact__eyebrow">Talk to a person</p>
            <h2>We are here for the practical questions.</h2>
            <dl className="contact__detail-list">
              <div>
                <dt>Email</dt>
                <dd><a href={`mailto:${siteConfig.contact.email}`}>{siteConfig.contact.email}</a></dd>
              </div>
              <div>
                <dt>Business address</dt>
                <dd>{siteConfig.contact.address}</dd>
              </div>
              <div>
                <dt>Opening hours</dt>
                <dd>{siteConfig.contact.hours}</dd>
              </div>
              <div>
                <dt>Operating country</dt>
                <dd>{siteConfig.operatingCountry}</dd>
              </div>
            </dl>
            <p className="contact__response-note">
              Please include an order number when your question relates to an existing order.
            </p>
          </aside>

          <section className="contact__form-section" aria-labelledby="contact-form-title">
            <div className="contact__form-heading">
              <p className="contact__eyebrow">Send an enquiry</p>
              <h2 id="contact-form-title">Write to our team</h2>
              <p>Required fields are marked with an asterisk (*).</p>
            </div>

            {submittedName && (
              <div className="contact__success" role="status" tabIndex="-1">
                <span className="contact__success-icon" aria-hidden="true">✓</span>
                <div>
                  <h3>Thanks, {submittedName}.</h3>
                  <p>Your email app should now be open with the enquiry prepared for you to send.</p>
                </div>
              </div>
            )}

            <form className="contact__form" onSubmit={handleSubmit} noValidate>
              <div className="contact__field-row">
                <div className="contact__field">
                  <label htmlFor="firstName">First name *</label>
                  <input
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    autoComplete="given-name"
                    disabled={isSubmitting}
                    {...errorProps("firstName")}
                  />
                  {errors.firstName && <p id="firstName-error" className="contact__error">{errors.firstName}</p>}
                </div>
                <div className="contact__field">
                  <label htmlFor="lastName">Last name *</label>
                  <input
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    autoComplete="family-name"
                    disabled={isSubmitting}
                    {...errorProps("lastName")}
                  />
                  {errors.lastName && <p id="lastName-error" className="contact__error">{errors.lastName}</p>}
                </div>
              </div>

              <div className="contact__field">
                <label htmlFor="email">Email address *</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  autoComplete="email"
                  inputMode="email"
                  disabled={isSubmitting}
                  {...errorProps("email")}
                />
                {errors.email && <p id="email-error" className="contact__error">{errors.email}</p>}
              </div>

              <div className="contact__field">
                <label htmlFor="subject">What can we help with? *</label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  {...errorProps("subject")}
                >
                  <option value="">Select a topic</option>
                  <option value="product">Product advice</option>
                  <option value="order">An order or delivery</option>
                  <option value="return">A return or refund</option>
                  <option value="website">Website support</option>
                  <option value="other">Something else</option>
                </select>
                {errors.subject && <p id="subject-error" className="contact__error">{errors.subject}</p>}
              </div>

              <div className="contact__field">
                <div className="contact__label-line">
                  <label htmlFor="message">Message *</label>
                  <span aria-live="polite">{formData.message.length}/1200</span>
                </div>
                <textarea
                  id="message"
                  name="message"
                  rows="7"
                  maxLength="1200"
                  value={formData.message}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                  {...errorProps("message")}
                />
                {errors.message && <p id="message-error" className="contact__error">{errors.message}</p>}
              </div>

              <p className="contact__privacy-note">
                Submitting opens your email app so you can send the message directly. Read our
                {" "}<Link to="/privacy" onClick={toTop}>privacy policy</Link> for more information.
              </p>
              <button className="contact__submit" type="submit" disabled={isSubmitting}>
                {isSubmitting ? <><span className="contact__spinner" aria-hidden="true" />Sending…</> : <>Send enquiry <span aria-hidden="true">→</span></>}
              </button>
            </form>
          </section>
        </div>
      </div>
    </main>
  );
}

export default Contact;
